import React, { useState, useRef, useEffect, useCallback } from "react";
import LogoUni from "../assets/image/University-of-Isfahan-Logo.png";
import RequestComponent from './RequestComponent';
import NotificationComponent from './NotificationComponent';
import AddMeetingModal from "../../Home/Components/AddMeetingModal";
import ProfileModal from "./ProfileModal";
import api from '../../api/api';
import { useAuth } from '../../context/AuthContext';
import AddRequestModal from "../../Home/Components/AddRequestModal";

function Header({ onMenuToggle }) {
  const { user } = useAuth();
  const [chatOpen, setChatOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const chatRef = useRef(null);
  const notifRef = useRef(null);

  const [viewingRequest, setViewingRequest] = useState(null);
  const [viewingCreatorProfile, setViewingCreatorProfile] = useState(null);
  const [creatorProfileLoading, setCreatorProfileLoading] = useState(false);
  const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // All data starts empty – will be fetched from API
  const [userProfile, setUserProfile] = useState(null);
  const [requests, setRequests] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [alertConfig, setAlertConfig] = useState({
    isOpen: false,
    title: "",
    message: "",
  });

  const getPendingRequests = useCallback((requestList) => requestList.filter((request) =>
    request.flows?.some(
      (flow) => flow.status === 'pending' && Number(flow.receiver) === Number(user?.id)
    )
  ), [user?.id]);

  const refreshNotifications = useCallback(async () => {
    const [taskResponse, meetingResponse] = await Promise.all([
      api.get('/task-notifications/'),
      api.get('/meeting-notifications/')
    ]);
    const taskNotifications = taskResponse.data.map((notification) => ({
      id: `task-${notification.id}`,
      backendId: notification.id,
      endpoint: 'task-notifications',
      type: 'task',
      text: `کار «${notification.task_title}» توسط ${notification.sender_name} به شما محول شد.`,
      timeAgo: new Date(notification.created_at).toLocaleString('fa-IR')
    }));
    const meetingNotifications = meetingResponse.data.map((notification) => ({
      id: `meeting-${notification.id}`,
      backendId: notification.id,
      endpoint: 'meeting-notifications',
      type: 'meeting',
      text: `شما به جلسه «${notification.meeting_title}» توسط ${notification.sender_name} دعوت شدید.`,
      timeAgo: new Date(notification.created_at).toLocaleString('fa-IR')
    }));
    setNotifications([...meetingNotifications, ...taskNotifications]);
  }, []);

  const refreshRequests = useCallback(async () => {
    const reqRes = await api.get('/requests/');
    setRequests(getPendingRequests(reqRes.data));
    await refreshNotifications();
  }, [getPendingRequests, refreshNotifications]);

  // Fetch real data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch user profile (if you have /users/me endpoint)
        if (user) {
          const profileRes = await api.get(`/users/${user.id}/`);
          setUserProfile(profileRes.data);
        }
        // Fetch requests (inbox)
        await refreshRequests();
      } catch (error) {
        console.error('Error fetching header data:', error);
      }
    };
    fetchData();
  }, [user, refreshRequests]);

  useEffect(() => {
    if (!user) return undefined;
    const intervalId = setInterval(() => {
      refreshRequests().catch((error) => {
        console.error('Error refreshing header requests:', error);
      });
    }, 10000);
    return () => clearInterval(intervalId);
  }, [user, refreshRequests]);


  const respondToRequest = async (flowId, status, comment) => {
  try {
    await api.patch(`/request-flows/${flowId}/`, { status, comment: comment || '' });
    const message = status === 'approved'
      ? 'درخواست با موفقیت تایید شد.'
      : status === 'rejected'
        ? 'درخواست رد شد.'
        : 'درخواست با موفقیت ارجاع شد.';
    handleRequestAction(null, message);
    await refreshRequests();
    setViewingRequest(null);
  } catch (error) {
    console.error('خطا در پاسخ به درخواست:', error);
    setAlertConfig({ isOpen: true, title: "خطا", message: "خطا در ثبت پاسخ. دوباره تلاش کنید." });
  }
};
  const handleRequestAction = (id, message) => {
  if (id) setRequests(prev => prev.filter(req => req.id !== id));
  setAlertConfig({ isOpen: true, title: "عملیات موفق", message });
};

  const handleNotificationRead = async (notification) => {
    try {
      await api.patch(`/${notification.endpoint}/${notification.backendId}/`, { is_read: true });
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    } catch (error) {
      console.error('خطا در ثبت خوانده‌شدن اعلان:', error);
    }
  };

  const handleViewCreatorProfile = async (creatorId) => {
    if (!creatorId) return;
    setCreatorProfileLoading(true);
    try {
      const res = await api.get(`/users/${creatorId}/`);
      const u = res.data;
      setViewingCreatorProfile({
        ...u,
        avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(u.name || u.username)}&background=random&color=fff&size=128`
      });
    } catch (error) {
      console.error('خطا در دریافت پروفایل درخواست‌دهنده:', error);
    } finally {
      setCreatorProfileLoading(false);
    }
  };

  const closeAlert = () => {
    setAlertConfig(prev => ({ ...prev, isOpen: false }));
  };

  const handleSave = (updatedData) => {
    setUserProfile(updatedData);
    // Optionally save to API
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (alertConfig.isOpen) return;
      if (chatRef.current && !chatRef.current.contains(event.target)) setChatOpen(false);
      if (notifRef.current && !notifRef.current.contains(event.target)) setNotifOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [alertConfig.isOpen]);

  const unreadNotifCount = notifications.length;
  const requestCount = requests.length;

  return (
    <header className="w-full h-20 bg-[#0f172a] flex items-center justify-between px-4 md:px-6 z-30 relative" dir="rtl">
      {/* راست: دکمه منو (موبایل) + لوگو */}
      <div className="flex items-center gap-3">
        <button
          onClick={onMenuToggle}
          className="md:hidden p-2 rounded-xl text-slate-200 hover:bg-white/10 transition-colors"
          aria-label="باز کردن منو"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <img src={LogoUni} alt="دانشگاه اصفهان" className="h-10 w-auto" />
        <span className="hidden sm:block text-white font-bold text-sm">سامانه زمان‌بندی</span>
      </div>

      {/* چپ: نوتیف، درخواست‌ها، پروفایل */}
      <div className="flex items-center gap-2 md:gap-3">
        {/* نوتیفیکیشن */}
        <div className="relative" ref={notifRef}>
          <button
            onClick={() => { setNotifOpen((v) => !v); setChatOpen(false); }}
            className="relative p-2.5 rounded-xl text-slate-200 hover:bg-white/10 transition-colors"
            aria-label="نوتیفیکیشن‌ها"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.4-1.4A2 2 0 0 1 18 14.2V11a6 6 0 1 0-12 0v3.2a2 2 0 0 1-.6 1.4L4 17h5m6 0v1a3 3 0 1 1-6 0v-1m6 0H9" />
            </svg>
            {unreadNotifCount > 0 && (
              <span className="absolute -top-0.5 -left-0.5 w-4 h-4 rounded-full bg-rose-500 text-[10px] text-white flex items-center justify-center font-bold">
                {unreadNotifCount}
              </span>
            )}
          </button>

          {notifOpen && (
            <div className="absolute left-0 mt-2 w-80 max-h-96 overflow-y-auto bg-white rounded-2xl shadow-2xl p-3 space-y-2 z-50">
              <h3 className="text-xs font-bold text-slate-500 px-1 mb-1">نوتیفیکیشن‌ها</h3>
              {notifications.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-6">نوتیفیکیشنی وجود ندارد</p>
              ) : (
                notifications.map((n) => (
                  <NotificationComponent key={n.id} notification={n} onRead={handleNotificationRead} />
                ))
              )}
            </div>
          )}
        </div>

        {/* درخواست‌ها */}
        <div className="relative" ref={chatRef}>
          <button
            onClick={() => { setChatOpen((v) => !v); setNotifOpen(false); }}
            className="relative p-2.5 rounded-xl text-slate-200 hover:bg-white/10 transition-colors"
            aria-label="درخواست‌ها"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {requestCount > 0 && (
              <span className="absolute -top-0.5 -left-0.5 w-4 h-4 rounded-full bg-indigo-500 text-[10px] text-white flex items-center justify-center font-bold">
                {requestCount}
              </span>
            )}
          </button>

          {chatOpen && (
            <div className="absolute left-0 mt-2 w-80 max-h-96 overflow-y-auto bg-white rounded-2xl shadow-2xl p-3 space-y-2 z-50">
              <h3 className="text-xs font-bold text-slate-500 px-1 mb-1">درخواست‌ها</h3>
              {requests.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-6">درخواستی وجود ندارد</p>
              ) : (
                requests.map((r) => (
                  <RequestComponent
  key={r.id}
  request={r}
  onQuickAction={respondToRequest}
  onView={() => { setViewingRequest(r); setChatOpen(false); }}
  onViewProfile={(creatorId) => { handleViewCreatorProfile(creatorId); setChatOpen(false); }}
/>
                ))
              )}
            </div>
          )}
        </div>

        {/* پروفایل */}
        <button
          onClick={() => setIsSettingsOpen(true)}
          className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/20 hover:border-indigo-400 transition-colors"
          aria-label="پروفایل"
        >
          {userProfile?.avatar ? (
            <img src={userProfile.avatar} alt="پروفایل" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-indigo-600 flex items-center justify-center text-white text-sm font-bold">
              {userProfile?.name?.charAt(0) || user?.name?.charAt(0) || "?"}
            </div>
          )}
        </button>
      </div>

      {/* مودال جزئیات درخواست */}
      {viewingRequest && (
  <AddRequestModal
    requestData={viewingRequest}
    respondMode={true}
    onRespond={respondToRequest}
    onClose={() => { setViewingRequest(null); refreshRequests(); }}
  />
)}

      {/* مودال افزودن جلسه (از دل درخواست) */}
      {isMeetingModalOpen && (
        <AddMeetingModal isOpen={isMeetingModalOpen} onClose={() => setIsMeetingModalOpen(false)} />
      )}

      {/* مودال پروفایل */}
      <ProfileModal
        user={userProfile || user}
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        onSave={handleSave}
        canEdit={true}
      />

      {/* مودال پروفایل درخواست‌دهنده (از نوتیفیکیشن/درخواست‌ها) */}
      {viewingCreatorProfile && (
        <ProfileModal
          user={viewingCreatorProfile}
          isOpen={!!viewingCreatorProfile}
          onClose={() => setViewingCreatorProfile(null)}
          canEdit={false}
        />
      )}

      {/* پیام موفقیت */}
      {alertConfig.isOpen && (
        <div className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4" dir="rtl">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl text-center">
            <h3 className="font-bold text-slate-800 mb-2">{alertConfig.title}</h3>
            <p className="text-sm text-slate-500 mb-5">{alertConfig.message}</p>
            <button
              onClick={closeAlert}
              className="w-full py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-bold"
            >
              باشه
            </button>
          </div>
        </div>
      )}
    </header>
  );
}

export default Header;